/**
 * Colored pills for Stage, Industry and Investors.
 *
 * Same label always gets the same color. Funding stages use a fixed hue so
 * Series A, Seed, Post-IPO and the rest stay easy to tell apart.
 */

type PillKind = "industry" | "investors" | "stage";

type PillColor = { bg: string; text: string; border: string };

const PILL_NEUTRAL: PillColor = {
  bg: "#f4f4f5",
  text: "#52525b",
  border: "rgba(82, 82, 91, 0.18)",
};

const FUNDING_STAGE_HUES: Record<string, number> = {
  "pre-seed": 18,
  seed: 42,
  angel: 62,
  "series a": 88,
  "series b": 118,
  "series c": 148,
  "series d": 178,
  "series e": 208,
  "series f": 238,
  "series g": 268,
  "series h": 298,
  growth: 318,
  "late stage": 334,
  "post-ipo": 352,
  ipo: 8,
  public: 28,
  debt: 198,
  grant: 52,
  bootstrap: 72,
  acquired: 328,
};

function fnv1aHash(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function normalizePillLabel(raw: string): string {
  let label = raw.trim().toLowerCase().replace(/\s+/g, " ").replace(/[._]/g, " ");
  if (/^post\s*i\.?\s*p\.?\s*o?\.?$/i.test(label) || label === "post ip") return "post-ipo";
  if (label === "pre seed") return "pre-seed";
  if (label === "late stage" || label === "late-stage") return "late stage";
  const series = label.match(/^series\s*([a-h])$/i);
  if (series) return `series ${series[1]!.toLowerCase()}`;
  return label;
}

function pillColorFromHue(hue: number): PillColor {
  const h = ((hue % 360) + 360) % 360;
  return {
    bg: `hsl(${h} 68% 93%)`,
    text: `hsl(${h} 58% 28%)`,
    border: `hsl(${h} 58% 28% / 0.22)`,
  };
}

function colorFor(value: string, kind: PillKind): PillColor {
  const label = normalizePillLabel(value);
  if (!label) return PILL_NEUTRAL;

  if (kind === "stage") {
    const stageHue = FUNDING_STAGE_HUES[label];
    if (stageHue !== undefined) return pillColorFromHue(stageHue);
  }

  const hue = Math.round((fnv1aHash(label) * 137.508) % 360);
  return pillColorFromHue(hue);
}

function splitValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (value === null || value === undefined || value === "" || value === "—" || value === "NULL") {
    return [];
  }
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item && item !== "—" && item !== "NULL");
}

export function Pill({ value, kind }: { value: string; kind: PillKind }) {
  const color = colorFor(value, kind);
  return (
    <span
      className="pill inline-flex max-w-[140px] shrink-0 truncate rounded px-1.5 py-px text-[12px] font-medium leading-4"
      style={{ background: color.bg, color: color.text, border: `1px solid ${color.border}` }}
      title={value}
    >
      {value}
    </span>
  );
}

export function Pills({
  value,
  kind,
}: {
  value: unknown;
  kind: PillKind;
}) {
  const items = splitValues(value);
  if (items.length === 0) return <span className="text-muted-foreground">—</span>;

  return (
    <span className="pills-row flex min-w-0 items-center gap-1 overflow-hidden">
      {items.map((item) => (
        <Pill key={item} value={item} kind={kind} />
      ))}
    </span>
  );
}
